import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, XCircle, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';

function statusIcon(status) {
  switch (status) {
    case 'approved': return <CheckCircle2 className="w-4 h-4 text-[#15803D]" />;
    case 'rejected': return <XCircle className="w-4 h-4 text-[#B91C1C]" />;
    default: return <Clock className="w-4 h-4 text-[#0B4F3F]" />;
  }
}

export default function AdminKYCQueue() {
  const { api } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewNotes, setReviewNotes] = useState({});
  const [reviewingId, setReviewingId] = useState(null);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/kyc-applications', {
        params: { status: filter === 'all' ? undefined : filter },
      });
      setApplications(data.applications || []);
    } catch {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleReview = async (appId, status) => {
    setReviewingId(appId);
    try {
      await api.put(`/admin/kyc/${appId}/review`, {
        status,
        admin_notes: reviewNotes[appId] || null,
      });
      toast.success(`Application ${status}`);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-kyc-queue">
      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList className="w-full bg-[#EFF2F7] rounded-xl p-1 h-auto">
          <TabsTrigger value="pending" data-testid="tab-pending" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all" className="flex-1 rounded-lg text-xs data-[state=active]:bg-[#F6F8FB] data-[state=active]:text-[#0B4F3F] data-[state=active]:shadow-sm">All</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4 space-y-3">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 animate-pulse space-y-3">
                <div className="h-4 bg-[#EFF2F7] rounded w-1/2" />
                <div className="h-3 bg-[#EFF2F7] rounded w-3/4" />
              </div>
            ))
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
              <p className="text-sm text-[#5B6675]">No {filter} applications</p>
            </div>
          ) : (
            applications.map((app) => (
              <div
                key={app.id}
                data-testid={`kyc-app-${app.id}`}
                className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#0B1620]">{app.user_name}</span>
                      {statusIcon(app.status)}
                    </div>
                    <p className="text-xs text-[#5B6675] mt-0.5">{app.user_phone}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    app.status === 'pending' ? 'bg-[#0B4F3F]/10 text-[#0B4F3F]' :
                    app.status === 'approved' ? 'bg-[#15803D]/10 text-[#15803D]' :
                    'bg-[#B91C1C]/10 text-[#B91C1C]'
                  }`}>
                    {app.status.toUpperCase()}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-[#EFF2F7] rounded-lg p-2">
                    <p className="text-[#5B6675]">Aadhaar</p>
                    <p className="text-[#0B1620] font-medium">{app.aadhaar_number}</p>
                  </div>
                  <div className="bg-[#EFF2F7] rounded-lg p-2">
                    <p className="text-[#5B6675]">PAN</p>
                    <p className="text-[#0B1620] font-medium">{app.pan_number}</p>
                  </div>
                </div>
                <div className="text-xs bg-[#EFF2F7] rounded-lg p-2">
                  <p className="text-[#5B6675]">Farm</p>
                  <p className="text-[#0B1620] font-medium">{app.farm_address} {app.farm_size ? `(${app.farm_size})` : ''}</p>
                </div>

                {app.document_paths?.length > 0 && (
                  <div className="text-xs">
                    <p className="text-[#5B6675] mb-1">Documents: {app.document_paths.length} file(s)</p>
                  </div>
                )}

                {app.admin_notes && (
                  <div className="text-xs bg-[#EFF2F7] rounded-lg p-2">
                    <p className="text-[#5B6675]">Admin Notes</p>
                    <p className="text-[#0B1620]">{app.admin_notes}</p>
                  </div>
                )}

                {app.status === 'pending' && (
                  <div className="space-y-2 pt-2 border-t border-[#E4E8EF]">
                    <Textarea
                      data-testid={`review-notes-${app.id}`}
                      placeholder="Add review notes (optional)"
                      value={reviewNotes[app.id] || ''}
                      onChange={(e) => setReviewNotes(prev => ({ ...prev, [app.id]: e.target.value }))}
                      rows={2}
                      className="text-xs bg-[#F1F5FA] border-[#E4E8EF] rounded-lg resize-none"
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid={`approve-btn-${app.id}`}
                        onClick={() => handleReview(app.id, 'approved')}
                        disabled={reviewingId === app.id}
                        size="sm"
                        className="flex-1 h-9 bg-[#15803D] hover:bg-[#15803D]/90 text-[#F6F8FB] rounded-lg text-xs"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button
                        data-testid={`reject-btn-${app.id}`}
                        onClick={() => handleReview(app.id, 'rejected')}
                        disabled={reviewingId === app.id}
                        size="sm"
                        variant="outline"
                        className="flex-1 h-9 border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C]/5 rounded-lg text-xs"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
